
import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, ArrowUpCircle, ArrowDownCircle, CheckCircle } from 'lucide-react';
import { PackageOption } from '../../types/packageTypes';

interface PackageCardProps {
  pkg: PackageOption;
  selectedPackage: string | null;
  initialPackage: string | null;
  onSelect: (packageId: string) => void;
  packageOptions: PackageOption[]; // Add this to access all package options
}

const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  selectedPackage,
  initialPackage,
  onSelect,
  packageOptions,
}) => {
  
  const getSelectionType = (currentId: string, newId: string): 'upgrade' | 'downgrade' | 'current' => {
    if (currentId === newId) return 'current';
    
    // Find the current package and its tier
    const currentPackage = packageOptions.find(p => p.id === currentId);
    // The new package is the current component's package
    const newPackage = pkg;
    
    if (!currentPackage || !newPackage) return 'current';
    
    return newPackage.tier > currentPackage.tier ? 'upgrade' : 'downgrade';
  };
  
  const getButtonText = () => {
    const packageId = pkg.id;
    
    if (initialPackage === packageId) {
      return (
        <span className="flex items-center">
          <CheckCircle 
            size={16} 
            className={`mr-2 ${selectedPackage === packageId ? 'text-white' : ''}`} 
          />
          Current GoPlan
        </span>
      );
    }
    
    if (selectedPackage === packageId) {
      const selectionType = getSelectionType(initialPackage || '', packageId);
      
      if (selectionType === 'upgrade') {
        return (
          <span className="flex items-center">
            <ArrowUpCircle 
              size={16} 
              className="mr-2 text-white" 
            />
            Upgrade
          </span>
        );
      } else if (selectionType === 'downgrade') {
        return (
          <span className="flex items-center">
            <ArrowDownCircle 
              size={16} 
              className="mr-2 text-white" 
            />
            Downgrade
          </span>
        );
      }
      
      // This is a new case: if it's selected but not an upgrade or downgrade
      return (
        <span className="flex items-center">
          <CheckCircle 
            size={16} 
            className="mr-2 text-white" 
          />
          Selected
        </span>
      );
    }
    
    if (initialPackage) {
      const selectionType = getSelectionType(initialPackage, packageId);
      
      if (selectionType === 'upgrade') {
        return (
          <span className="flex items-center">
            <ArrowUpCircle 
              size={16} 
              className={`mr-2 ${selectedPackage === packageId ? 'text-white' : 'text-green-500'}`} 
            />
            Upgrade
          </span>
        );
      } else if (selectionType === 'downgrade') {
        return (
          <span className="flex items-center">
            <ArrowDownCircle 
              size={16} 
              className={`mr-2 ${selectedPackage === packageId ? 'text-white' : 'text-amber-500'}`} 
            />
            Downgrade
          </span>
        );
      }
    }
    
    return 'Select';
  };

  return (
    <div 
      className={`border-2 rounded-lg p-6 relative transition-all duration-200 flex flex-col bg-white
        ${selectedPackage === pkg.id 
          ? `${pkg.borderColor} shadow-md` 
          : 'border-gray-200 hover:border-gray-300'
        }
      `}
      onClick={() => onSelect(pkg.id)}
    >
      <div className="mb-4">
        <h3 className="text-lg font-bold mb-1">{pkg.title}</h3>
        {pkg.originalPrice ? (
          <div>
            <p className="text-2xl font-bold text-black">{pkg.price} <span className="text-sm text-green-600 font-medium">limited-time deal</span></p>
            <p className="text-lg text-gray-500 line-through">{pkg.originalPrice}</p>
          </div>
        ) : (
          <p className="text-2xl font-bold text-black">{pkg.price}</p>
        )}
      </div>
      
      <ul className="space-y-3 mb-6 flex-grow">
        {pkg.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button 
        variant={selectedPackage === pkg.id ? "default" : "outline"}
        className={`w-full mt-auto ${selectedPackage === pkg.id ? pkg.buttonColor : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(pkg.id);
        }}
        type="button"
      >
        {getButtonText()}
      </Button>
    </div>
  );
};

export default PackageCard;
